import { isArray, isUndefined, mergeWith } from 'lodash';

import {
    FeedFilterCriteriaKey,
    getFeedFilterFilterCriteria,
    StatisticsByAllGoals
} from '../declarations/filter/filter';
import { getStrictExpressions } from '../service/FilterHelpers/FilterHelpers';
import { getNumberValue } from '../service/FormatHelpers/FormatHelpers';
import { ClientData, FeedFilter, FeedFilterStats } from '../typings/domain';
import { FeedFilterPricesInfo, FeedFilterRateType } from '../typings/feedFilters';
import { FeedFiltersQueryFilter, FilterExpression, FilterPartialExpression, StatsQueryFilter } from '../typings/filter';
import {
    adsPhraseRowFragment,
    GdShowConditionAutobudgetPriorityInput,
    GdSmartFilterBaseStatusInput,
    GdSmartFilterTargetFunnelInput,
    GdUpdateSmartFiltersItemInput,
    SmartFiltersGridQuery_client_smartFilters_rowset,
    SmartFiltersGridQuery_client_smartFilters_rowset_stats
} from '../typings/graphql-grid';
import { GridSourceParams } from '../typings/grid';

import { mapRowsPhraseToBanners } from './banner/banner';
import { getValueFromApiFormat } from './common/common';
import { FilterCriteriaMappings, textToFeedFilterFilterExpression } from './filter/filter';
import {
    defineSmartFilterBudgetCalculation,
    mapAvailableConditionFromApiResponse,
    mapBidValueToApiFormat,
    mapCrossTabConditionsFromApiResponse,
    mapRowsToFeedFilterGroupTruncatedInfo,
    mapSmartFiltersConditionFromApiResponse
} from './group/group';

const mapStatsDataToStats = (stats: SmartFiltersGridQuery_client_smartFilters_rowset_stats): FeedFilterStats => {
    const { cost, goals, avgClickCost } = stats;

    return {
        goals: getNumberValue(goals),
        avgClickCost: getNumberValue(avgClickCost),
        cost: getNumberValue(cost)
    };
};

export function mapRowsToFeedFilters(
    filtersRows: SmartFiltersGridQuery_client_smartFilters_rowset[],
    clientInfo: ClientData,
    params: GridSourceParams
): Array<FeedFilter> {
    return filtersRows.map(filter => {
        const {
            name,
            id,
            tab,
            conditions,
            targetFunnel,
            priceCpc,
            priceCpa,
            adGroup,
            autobudgetPriority,
            stats,
            status
        } = filter;
        const { mainAd, adsCount, campaign } = adGroup;

        const bannerRows: adsPhraseRowFragment[] = mainAd ? [mainAd] : [];

        const [mainBanner] = mapRowsPhraseToBanners(bannerRows, clientInfo, params, adGroup);

        const formattedPriceCpc = getValueFromApiFormat(priceCpc);
        const formattedPriceCpa = getValueFromApiFormat(priceCpa);

        const resultConditions = conditions.map(mapSmartFiltersConditionFromApiResponse);

        return {
            ...mapCrossTabConditionsFromApiResponse(resultConditions, tab),
            isAvailable: mapAvailableConditionFromApiResponse(resultConditions),
            feedCategories: [],
            tab,
            name,
            autobudgetPriority: getValueFromApiFormat(autobudgetPriority),
            id,
            targetFunnel,
            budgetCalculation: defineSmartFilterBudgetCalculation(campaign.strategy.strategyType),
            mainBanner,
            bannersTotalCount: adsCount,
            stats: mapStatsDataToStats(stats),
            status: status.primaryStatus,
            group: mapRowsToFeedFilterGroupTruncatedInfo(adGroup, clientInfo, params),
            priceCpc: isUndefined(formattedPriceCpc) ? formattedPriceCpc : Number(formattedPriceCpc),
            priceCpa: isUndefined(formattedPriceCpa) ? formattedPriceCpa : Number(formattedPriceCpa)
        };
    });
}

const mappings = new FilterCriteriaMappings<FeedFiltersQueryFilter & StatsQueryFilter>();
const { toNumber, toEnum } = mappings.valueMappings;

const feedFilterCriteriaMappings: {
    [key in FeedFilterCriteriaKey]: (expression: FilterExpression) => FeedFiltersQueryFilter;
} = {
    // todo @mrronuar добавить smartFilterIdNotIn после поддержки на бэкенде DIRECT-96478
    id: mappings.includes('smartFilterIdIn', 'smartFilterIdIn'),
    name: mappings.contains('nameContains', 'nameNotContains'),
    status: mappings.equality('statusIn', toEnum(GdSmartFilterBaseStatusInput)),
    autobudgetPriority: mappings.equality('autobudgetPriorityIn', toEnum(GdShowConditionAutobudgetPriorityInput)),
    targetFunnel: mappings.equality('targetFunnelIn', toEnum(GdSmartFilterTargetFunnelInput)),
    priceCpc: mappings.comparison('minPriceCpc', 'maxPriceCpc', toNumber, false),
    priceCpa: mappings.comparison('minPriceCpa', 'maxPriceCpa', toNumber, false),
    cost: mappings.comparison('minCost', 'maxCost', toNumber),
    avgClickCost: mappings.comparison('minAvgClickCost', 'maxAvgClickCost', toNumber),
    goals: (expression: FilterExpression): FeedFiltersQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison('minGoals', 'maxGoals', toNumber, false, expression.additionalCriteriaKey)(
                expression
            );
        }

        return mappings.comparison('minGoals', 'maxGoals', toNumber)(expression);
    }
};

const expressionToFeedFiltersQueryFilter = (expression: FilterExpression): FeedFiltersQueryFilter => {
    return feedFilterCriteriaMappings[expression.criteriaKey](expression);
};

export const mapFeedFilterFilterToApiFormat = (expressions: FilterPartialExpression[] = []): FeedFiltersQueryFilter => {
    const criteria = getFeedFilterFilterCriteria();

    return getStrictExpressions(expressions, criteria, textToFeedFilterFilterExpression)
        .map(expressionToFeedFiltersQueryFilter)
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {}
        );
};

export function mapFeedFilterRateToPrice(rateType: FeedFilterRateType, rate?: number): { [key: string]: number } {
    const priceField = rateType === FeedFilterRateType.cpa ? 'priceCpa' : 'priceCpc';

    return {
        [priceField]: mapBidValueToApiFormat(rate)
    };
}

export function mapGridFeedFilterPricesToApiFormat(
    feedFilterPrices: FeedFilterPricesInfo
): GdUpdateSmartFiltersItemInput {
    const { autobudgetPriority, priceCpa, priceCpc, id } = feedFilterPrices;
    const resultFilter: GdUpdateSmartFiltersItemInput = { id };

    if (autobudgetPriority) {
        resultFilter.autobudgetPriority = GdShowConditionAutobudgetPriorityInput[autobudgetPriority];
    }

    if (!isUndefined(priceCpa)) {
        resultFilter.priceCpa = String(priceCpa);
    }

    if (!isUndefined(priceCpc)) {
        resultFilter.priceCpc = String(priceCpc);
    }

    return resultFilter;
}
